import type {
  CoverData,
  DesignTemplate,
  DesignTemplateSummary,
} from '@mathflat/domain/DesignTemplate'
import { cloneDeep } from 'lodash-es'
import { observable } from 'mobx'

import type { Tuple } from '@/types'

export class WorkbookDesignTemplate {
  id: DesignTemplate['id']
  type: DesignTemplate['type']
  languageType: DesignTemplate['languageType']
  categories: DesignTemplate['categories']
  color: DesignTemplate['color']
  thumbnailImageUrl: DesignTemplate['thumbnailImageUrl']
  coverData: CoverData
  pageSize = 100

  constructor(data: DesignTemplate | WorkbookDesignTemplate) {
    this.id = data.id
    this.type = data.type
    this.languageType = data.languageType
    this.categories = data.categories
    this.color = data.color
    this.thumbnailImageUrl = data.thumbnailImageUrl
    // 편집기가 표지 항목을 제자리에서 고치고 화면이 그 변화를 따라가야 해서 깊게 관찰한다
    this.coverData = observable(cloneDeep(data.coverData))
    if ('pageSize' in data && data.pageSize) {
      this.pageSize = data.pageSize
    }
  }

  setCoverData(coverData: CoverData) {
    this.coverData = coverData
  }

  toSummary(): DesignTemplateSummary {
    return {
      id: this.id,
      type: this.type,
      languageType: this.languageType,
      categories: this.categories,
      color: this.color,
      thumbnailImageUrl: this.thumbnailImageUrl,
    }
  }

  static font = {
    fontFamilies: [
      ['리디바탕', 'RIDIBatang'],
      ['부크크명조', 'BookkMyungjo-Bold'],
      ['제주명조', 'JejuMyeongjo'],
      ['충주김생체', 'ChungjuKimSaeng'],
      ['프리텐다드 light', 'Pretendard-Light'],
      ['프리텐다드 regular', 'Pretendard-Regular'],
      ['프리텐다드 medium', 'Pretendard-Medium'],
      ['프리텐다드 semibold', 'Pretendard-SemiBold'],
      ['프리텐다드 bold', 'Pretendard-Bold'],
      ['오아고딕 extrabold', 'OAGothic-ExtraBold'],
      ['프리젠테이션 medium', 'Freesentation-Medium'],
      ['프리젠테이션 semibold', 'Freesentation-SemiBold'],
      ['프리젠테이션 bold', 'Freesentation-Bold'],
      ['플라이트 산스 regular', 'FlightSans-Regular'],
      ['플라이트 산스 bold', 'FlightSans-Bold'],
      ['HS산토끼체 2.0 Regular', 'HSSantoki-Regular'],
      ['SB 어그로 Light', 'Aggravo-Light'],
      ['MICE 명조 Regular', 'MiceMyungjo-Regular'],
      ['머니그라피 Rounded', 'Moneygraphy-Rounded'],
      ['하는체 light', 'Do-Light'],
      ['하는체 regular', 'Do-Regular'],
      ['하는체 bold', 'Do-Bold'],
      ['아는체 regular', 'Know-Regular'],
      ['아는체 bold', 'Know-Bold'],
      ['서강체', 'SOGANGUNIVERSITY-Regular'],
      ['해옹체 Sans', 'KMU80-HaeongSans'],
      ['카페24 모야모야', 'Cafe24Moyamoya'],
      ['G마켓 산스 Medium', 'GmarketSans-Medium'],
    ] as const,
    fontSizes: [8, 12, 14, 16, 24, 32, 40, 48, 56],
  }

  static templateColors: Tuple<WorkbookDesignTemplate['color'], string>[] = [
    ['WHITE', '#fff'],
    ['SKY', '#37bef0'],
    ['BLUE_GREEN', '#00ada9'],
    ['GREEN', '#43b149'],
    ['PINK', '#e098c1'],
    ['ORANGE', '#ee782c'],
    ['YELLOW', '#fcc80e'],
  ]

  static templateColorMap = new Map(WorkbookDesignTemplate.templateColors)
}
